import React, { useState, useEffect, useRef } from "react";
import QRCode from "qrcode";
import * as api from "../api";
import { trackUrl, generateReceiptPDF, sendReceiptViaWhatsApp, setReceiptSizesCache, setBrandingCache } from "../utils";
import { C, R, F, numeric, monoRef } from "../theme";
import { Icon } from "../icons";
import { Btn, Badge, Callout, Modal } from "./UI";

// Cible tactile minimale — écran utilisé sur le terrain, souvent en plein soleil.
const TAP = { minHeight: 46 };

function ColRecu({ setView, order }) {
  const canvasRef  = useRef(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waError,   setWaError]   = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(null); // "print" | "share" | null

  useEffect(() => {
    if (canvasRef.current && order) {
      QRCode.toCanvas(canvasRef.current, trackUrl(order.id), {
        width: 320, margin: 1, errorCorrectionLevel: "H", color: { dark: "#000000", light: "#ffffff" },
      });
    }
  }, [order]);

  useEffect(() => {
    api.getSettings().then(s => {
      if (s && s.receipt_font_sizes) {
        try { setReceiptSizesCache(JSON.parse(s.receipt_font_sizes)); } catch {}
      }
      if (s) setBrandingCache({ phone_main: s.phone_main, phone_whatsapp: s.phone_whatsapp });
    }).catch(() => {});
  }, []);

  async function printPDF(sizeProfile = "normal") {
    setShowSizePicker(null);
    setPdfLoading(true);
    try {
      const doc = await generateReceiptPDF(order, "a4", sizeProfile);
      doc.save(`Recu_${order.id}.pdf`);
    } catch (err) { console.error("PDF error:", err); alert("Erreur lors de la génération du reçu"); }
    finally { setPdfLoading(false); }
  }

  async function sharePDF(sizeProfile = "normal") {
    setShowSizePicker(null);
    setShareLoading(true);
    try {
      const doc = await generateReceiptPDF(order, "a4", sizeProfile);
      const blob = doc.output("blob");
      const file = new File([blob], `Recu_${order.id}.pdf`, { type: "application/pdf" });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: `Reçu Setal Express ${order.id}`, files: [file] });
      } else { doc.save(`Recu_${order.id}.pdf`); }
    } catch (err) { if (err.name !== "AbortError") console.error(err); }
    finally { setShareLoading(false); }
  }

  if (!order) return <div style={{ padding: 40, textAlign: "center", color: C.inkMuted }}>Aucune commande</div>;
  const items = order.items ? order.items.split(",").filter(Boolean) : [];
  const payLabel = { non_paye: "Non payé", avance: "Avance versée", paye: "Payé" }[order.payment_status] || "";
  const payColor = { non_paye: C.danger, avance: C.warning, paye: C.success }[order.payment_status] || C.inkMuted;

  // Ligne d'information du ticket
  const line = (icon, label, value) => (
    <div key={label} style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: F.size.base,
    }}>
      <Icon name={icon} size={14} color={C.inkMuted} />
      <span style={{ fontWeight: F.weight.medium, color: C.inkBody, minWidth: 78 }}>{label}</span>
      <span className="truncate" style={{ color: C.inkMuted, flex: 1, textAlign: "right" }}>{value}</span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      {/* ─── Confirmation ────────────────────────────────────────────────── */}
      <div className="no-print" style={{ background: C.navy, color: C.inkOn, padding: "20px 16px" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: R.lg, flexShrink: 0,
            background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="success" size={20} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.01em" }}>
              {order.order_type === "sur_place" ? "Réception validée" : "Collecte validée"}
            </h1>
            <p style={{ fontSize: F.size.md, color: "rgba(255,255,255,.72)", marginTop: 2 }}>
              Commande <span style={{ ...monoRef, color: C.inkOn, fontSize: F.size.md }}>{order.id}</span>
            </p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 480, margin: "0 auto", padding: 16 }}>
        {/* ─── Ticket ────────────────────────────────────────────────────── */}
        <div className="ticket animate-fadeInUp" style={{ marginBottom: 14 }}>
          {/* En-tête du ticket */}
          <div style={{ padding: "14px 16px", background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: F.size.xs, fontWeight: F.weight.semi, letterSpacing: ".06em",
                  textTransform: "uppercase", color: C.inkMuted,
                }}>
                  Reçu de collecte
                </div>
                <div style={{ ...monoRef, fontSize: F.size.xl, fontWeight: F.weight.semi, color: C.ink, marginTop: 2 }}>
                  {order.id}
                </div>
              </div>
              <Badge status={order.status} />
            </div>
          </div>

          {/* Coordonnées client */}
          <div style={{ padding: "6px 16px 12px" }}>
            <div style={{ padding: "10px 0 4px", fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium }}>
              Client
            </div>
            <div style={{ fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink, marginBottom: 6 }}>
              {order.client}
            </div>
            {line("phone", "Téléphone", <span style={numeric}>{order.phone}</span>)}
            {line("location", "Adresse", (order.address || "").substring(0, 40) + ((order.address || "").length > 40 ? "…" : ""))}
            {line("calendar", "Date", <span style={numeric}>{order.date} à {order.time}</span>)}
          </div>

          {/* Articles */}
          {items.length > 0 && (
            <>
              <div className="ticket-dashed" />
              <div style={{ padding: "12px 16px" }}>
                <div className="icon-text" style={{ fontSize: F.size.sm, fontWeight: F.weight.semi, color: C.inkMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>
                  <Icon name="order" size={13} />
                  Articles
                </div>
                {items.map((item, i) => {
                  const parts = item.trim().split(" x");
                  return (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                      padding: "8px 0",
                      borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : "none",
                      fontSize: F.size.md,
                    }}>
                      <span className="truncate" style={{ color: C.inkBody }}>{parts[0]?.trim()}</span>
                      <strong style={{ color: C.ink, flexShrink: 0, ...numeric }}>× {parts[1]?.trim() || "1"}</strong>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Montant */}
          {order.price && (
            <>
              <div className="ticket-dashed" />
              <div style={{ padding: "14px 16px", background: C.surfaceAlt }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontSize: F.size.base, color: C.inkMuted, fontWeight: F.weight.medium }}>Montant total</span>
                  <strong style={{ fontSize: F.size["2xl"], fontWeight: F.weight.bold, color: C.ink, letterSpacing: "-.02em", ...numeric }}>
                    {Number(order.price).toLocaleString()} FCFA
                  </strong>
                </div>
                {payLabel && (
                  <div style={{
                    marginTop: 8, display: "inline-flex", alignItems: "center", gap: 7,
                    background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.pill,
                    padding: "3px 11px",
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: payColor, flexShrink: 0 }} />
                    <span style={{ fontSize: F.size.sm, fontWeight: F.weight.semi, color: payColor }}>{payLabel}</span>
                    {order.payment_status === "avance" && order.amount_paid && (
                      <span style={{ fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>
                        ({Number(order.amount_paid).toLocaleString()} F versés)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Code QR de suivi */}
          <div className="ticket-dashed" />
          <div style={{ padding: "14px 16px 18px", textAlign: "center" }}>
            <div className="icon-text" style={{ justifyContent: "center", fontSize: F.size.sm, fontWeight: F.weight.semi, color: C.inkMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>
              <Icon name="qr" size={13} />
              Code de suivi
            </div>
            <div style={{ display: "inline-block", padding: 10, background: C.surface, borderRadius: R.md, border: `1px solid ${C.border}` }}>
              <canvas ref={canvasRef} style={{ display: "block", maxWidth: "100%", height: "auto" }} />
            </div>
            <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginTop: 10, lineHeight: 1.5 }}>
              Le client scanne ce code pour suivre sa commande en temps réel.
            </p>
          </div>
        </div>

        {/* ─── Actions (masquées à l'impression) ─────────────────────────── */}
        <div className="no-print">
          {waError && (
            <Callout tone="danger" icon="alert" style={{ marginBottom: 10 }}>{waError}</Callout>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <Btn
              variant="white" icon="download" style={TAP}
              onClick={() => setShowSizePicker("print")} disabled={pdfLoading} loading={pdfLoading}
            >
              {pdfLoading ? "Génération…" : "Télécharger PDF"}
            </Btn>
            <Btn
              variant="white" icon="share" style={TAP}
              onClick={() => setShowSizePicker("share")} disabled={shareLoading} loading={shareLoading}
            >
              {shareLoading ? "Partage…" : "Partager"}
            </Btn>
          </div>

          <Btn
            variant="success" icon="chat" fullWidth style={{ ...TAP, marginBottom: 10 }}
            onClick={() => sendReceiptViaWhatsApp(order, setWaLoading, setWaError)}
            disabled={waLoading} loading={waLoading}
          >
            {waLoading ? "Envoi du reçu…" : "Envoyer le reçu par WhatsApp"}
          </Btn>

          <Btn variant="ghost" icon="back" fullWidth style={{ minHeight: 50 }} onClick={() => setView("col_list")}>
            Retour au tableau de bord
          </Btn>
        </div>
      </div>

      {/* ─── Modale : taille d'impression ────────────────────────────────── */}
      {showSizePicker && (
        <Modal
          title="Taille d'impression"
          icon="print"
          width={380}
          onClose={() => setShowSizePicker(null)}
          footer={<Btn variant="ghost" style={TAP} onClick={() => setShowSizePicker(null)}>Annuler</Btn>}
        >
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 14 }}>
            Choisissez la taille adaptée à votre imprimante.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn
              variant="white" icon="document" style={{ minHeight: 52, flex: 1 }}
              onClick={() => showSizePicker === "print" ? printPDF("normal") : sharePDF("normal")}
            >Normal</Btn>
            <Btn
              icon="document" style={{ minHeight: 52, flex: 1 }}
              onClick={() => showSizePicker === "print" ? printPDF("grand") : sharePDF("grand")}
            >Grand</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default ColRecu;
